import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import withWidth from '@material-ui/core/withWidth';
import ReactSignaturePad from 'react-signature-pad';
import qs from 'qs';
import { getFormValues } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from 'components/common/Dialog';

import Button from 'components/common/Button';

import documents from 'config/documents';
import documentToPDF from 'utils/documentToPDF';
import templateToPDF from 'utils/templateToPDF';
import patientProfileToPDF from 'utils/patientProfileToPDF';
import patientInsuranceToPDF from 'utils/patientInsuranceToPDF';
import patientVitalsToPDF from 'utils/patientVitalsToPDF';

import { createPatientDocument } from 'redux/modules/patientsDocuments';
import { fetchOrder } from 'redux/modules/orders';
import { addNotification } from 'redux/modules/notifications';

import DocumentsSignDialogForm from './DocumentsSignDialogForm';

const styles = theme => ({
  signaturePad: {
    width: 700,
    height: 300,
    margin: 24,
    userSelect: 'none',
    border: `1px solid ${theme.palette.grey[600]}`,
    borderRadius: 4,
    [theme.breakpoints.down('sm')]: {
      margin: '16px auto',
      width: 'calc(100% - 16px)',
      height: 'calc(44vw - 16px)',
    },
    '& canvas': {
      width: 700,
      height: 300,
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        height: 'calc(44vw - 16px)',
      },
    },
  },
  canvas: {
    width: 700,
    height: 300,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      height: 'calc(44vw - 16px)',
    },
  },
  action: {
    '&:first-child': {
      marginRight: 'auto',
    },
  },
  form: {
    margin: 24,
    [theme.breakpoints.down('sm')]: {
      margin: '16px 8px',
    },
  },
  subheading: {
    padding: '16px 24px 0 24px',
  },
});

class DocumentsSignDialog extends Component {
  constructor(props) {
    super(props);

    this.state = {
      empty: true,
      submitting: false,
    };

    this.onSignEnd = this.onSignEnd.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onSaveButtonClick = this.onSaveButtonClick.bind(this);
    this.onEditPreviewClick = this.onEditPreviewClick.bind(this);
    this.onDoneButtonClick = this.onDoneButtonClick.bind(this);
    this.onDialogEnter = this.onDialogEnter.bind(this);
    this.onDialogEntered = this.onDialogEntered.bind(this);
  }

  componentWillMount() {
    window.addEventListener('resize', this.onResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  onSignEnd() {
    this.setState({
      empty: this.canvasRef.isEmpty(),
    });
  }

  onResize() {
    if (this.canvasRef) {
      this.setState({
        empty: this.canvasRef.isEmpty(),
      });
    }
  }

  onSaveButtonClick() {
    const { props } = this;
    const signatureImg = this.canvasRef.toDataURL('image/png');
    let documentTypes = [];
    let documentNames = [];
    let orderID;
    let fileName = 'documents.pdf';
    let pdfPromise;
    if (props.orders && props.orders.length > 0) {
      const ordersToSign = props.orders.filter(ordersItem =>
        props.formValues['Sign Order'].find(item => item.value === ordersItem.ID));
      orderID = ordersToSign.map(item => item.ID).join('-');
      pdfPromise = ordersToSign.reduce((orderAccumulator, currentOrder) => {
        return Object.keys(props.printFormValues).reduce((accumulator, currentValue) => {
          if (props.printFormValues[currentValue] === true) {
            if (currentValue.indexOf('Document') !== -1) {
              const documentID = Number(currentValue.split('_')[1]);
              const chunkOrderLine = currentValue.split('_')[2]
                ? currentValue.split('_')[2].split('-').map(item => Number(item))
                : null;
              const chunkOrderLineInCurrentOrder = chunkOrderLine
                ? currentOrder['Order Line'].filter(item => chunkOrderLine.indexOf(item.ID) !== -1)
                : null;
              const selectedDocument = documents
                .find(item => item.ID === documentID);
              if (documentTypes.indexOf(selectedDocument.Type) === -1) {
                documentTypes = [
                  ...documentTypes,
                  selectedDocument.Type,
                ];
              }
              if (documentNames.indexOf(selectedDocument.Name) === -1) {
                documentNames = [
                  ...documentNames,
                  selectedDocument.Name,
                ];
              }
              if (chunkOrderLine && chunkOrderLineInCurrentOrder.length === 0) {
                return accumulator;
              }
              return accumulator.then(prevPDF => documentToPDF({
                inputPDF: prevPDF,
                document: selectedDocument,
                patient: currentOrder.Patient,
                business: props.business,
                systemSettings: props.systemSettings,
                facility: currentOrder.Facility,
                insurance: currentOrder['Primary Insurance'],
                orderLine: chunkOrderLineInCurrentOrder && chunkOrderLineInCurrentOrder.length > 0
                  ? chunkOrderLineInCurrentOrder
                  : currentOrder['Order Line'],
                appInfo: props.appInfo,
                orderInsuranceParam: currentOrder['Order Insurance Param'],
                prescriber: props.Prescriber,
                signature: signatureImg,
                signatureDate: props.formValues && props.formValues['Date of Delivery'] ? props.formValues['Date of Delivery'] : '',
                account: props.account,
              }));
            }
          }
          return accumulator;
        }, orderAccumulator);
      }, new Promise(resolve => resolve()));
    } else {
      pdfPromise = Object.keys(props.printFormValues).reduce((accumulator, currentValue) => {
        if (props.printFormValues[currentValue] === true) {
          if (currentValue.indexOf('Document') !== -1) {
            const documentID = Number(currentValue.split('_')[1]);
            const orderLine = currentValue.split('_')[2]
              ? currentValue.split('_')[2].split('-').map(item => Number(item))
              : null;
            const selectedDocument = documents
              .find(item => item.ID === documentID);
            if (documentTypes.indexOf(selectedDocument.Type) === -1) {
              documentTypes = [
                ...documentTypes,
                selectedDocument.Type,
              ];
            }
            if (documentNames.indexOf(selectedDocument.Name) === -1) {
              documentNames = [
                ...documentNames,
                selectedDocument.Name,
              ];
            }
            if (orderLine && props['Order Line'] && props['Order Line'].length > 0) {
              orderID = props['Order Line'][0]['Order ID'];
            }
            return accumulator.then(prevPDF => documentToPDF({
              inputPDF: prevPDF,
              document: selectedDocument,
              patient: props.patient,
              diagnosis: props.diagnosis,
              business: props.business,
              systemSettings: props.systemSettings,
              facility: props.facility,
              insurance: props['Primary Insurance'],
              orderLine: orderLine
                ? props['Order Line'].filter(item => orderLine.indexOf(item.ID) !== -1)
                : props['Order Line'],
              appInfo: props.appInfo,
              orderInsuranceParam: props['Order Insurance Param'],
              prescriber: props.prescriber,
              signature: signatureImg,
              signatureDate: props.formValues && props.formValues['Date of Delivery'] ? props.formValues['Date of Delivery'] : '',
              account: props.account,
            }));
          }
          if (currentValue.indexOf('Template') !== -1) {
            const templateID = Number(currentValue.split('_')[1]);
            const selectedTemplate = this.props.templates
              .find(item => item.ID === templateID);
            return accumulator.then(prevPDF => templateToPDF({
              inputPDF: prevPDF,
              template: selectedTemplate,
              patient: props.patient,
              business: props.business,
              order: props.order,
              signature: signatureImg,
            }));
          }
          if (currentValue.indexOf('Patient Profile') !== -1) {
            return accumulator.then(prevPDF => patientProfileToPDF({
              inputPDF: prevPDF,
              patient: props.patient,
            }));
          }
          if (currentValue.indexOf('Patient Insurance') !== -1) {
            return accumulator.then(prevPDF => patientInsuranceToPDF({
              inputPDF: prevPDF,
              patient: props.patient,
            }));
          }
          if (currentValue.indexOf('Patient Vitals') !== -1) {
            return accumulator.then(prevPDF => patientVitalsToPDF({
              inputPDF: prevPDF,
              patient: props.patient,
            }));
          }
        }
        return accumulator;
      }, new Promise(resolve => resolve()));
    }
    this.setState({ submitting: true });
    documentTypes = documentTypes.map(documentTypesItem => {
      switch (documentTypesItem) {
        case 'Delivery Ticket':
          return 'Delivery Tickets';
          break;
        case 'Prior Authorization Template':
          return 'Prior Authorization Templates';
          break;
        case 'Assessment Form':
          return 'Assessment Forms';
          break;
        default:
          return documentTypesItem;
          break;
      }
    });
    if (documentNames.length === 1) {
      fileName = orderID ? `${documentNames[0]}-order-${orderID}.pdf` : `${documentNames[0]}.pdf`;
    } else if (documentTypes.length === 1) {
      fileName = orderID ? `${documentTypes[0]}-order-${orderID}.pdf` : `${documentTypes[0]}.pdf`;
    } else {
      fileName = orderID ? `${documentTypes.join('-')}-order-${orderID}.pdf` : `${documentTypes.join('-')}.pdf`;
    }
    pdfPromise.then((pdf) => {
      const fileBlob = pdf.output('blob');
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        fileBlob.lastModifiedDate = new Date();
        props.actions.createPatientDocument({
          values: {
            'Record Number': props.orders && props.orders.length > 0
              ? props.orders[0].Patient['Record Number'] : props.patient['Record Number'],
            Document: fileBlob,
            Name: fileName,
          },
          resolve: () => {
            this.setState({ submitting: false });
            props.actions.addNotification({
              message: props.orders && props.orders.length > 0
                ? `"${fileName}" was saved to Documents in ${props.orders[0].Patient['First Name']} ${props.orders[0].Patient['Last Name']} charts`
                : `"${fileName}" was saved to Documents in ${props.patient['First Name']} ${props.patient['Last Name']} chart`,
              variant: 'success',
            });
            props.onClose();
          },
          reject: () => {
            this.setState({ submitting: false });
          },
        });
      } else {
        const file = new File(
          [fileBlob],
          fileName,
          { type: 'application/pdf', lastModified: Date.now() },
        );
        props.actions.createPatientDocument({
          values: {
            'Record Number': props.orders && props.orders.length > 0
              ? props.orders[0].Patient['Record Number'] : props.patient['Record Number'],
            Document: file,
          },
          resolve: () => {
            this.setState({ submitting: false });
            props.actions.addNotification({
              message: props.orders && props.orders.length > 0
                ? `"${fileName}" was saved to Documents in ${props.orders[0].Patient['First Name']} ${props.orders[0].Patient['Last Name']} charts`
                : `"${fileName}" was saved to Documents in ${props.patient['First Name']} ${props.patient['Last Name']} chart`,
              variant: 'success',
            });
            props.onClose();
          },
          reject: () => {
            this.setState({ submitting: false });
          },
        });
      }
    });
  }

  onEditPreviewClick() {
    const {
      printFormValues,
      variant,
      patient,
      ID,
      orders,
      formValues,
    } = this.props;
    let ordersToSign = orders && orders.length > 0
      ? orders.filter(ordersItem => formValues['Sign Order'].find(item => item.value === ordersItem.ID))
      : null;
    let ordersToPreview = ordersToSign && ordersToSign.length > 0
      ? ordersToSign.reduce((accumulator, currentOrder) => {
        return {
          ...accumulator,
          [currentOrder.ID]: {},
        };
      }, {}) : null;
    const editPreviewItems = Object.keys(printFormValues)
      .reduce((accumulator, currentValue) => {
        if (printFormValues[currentValue] === true) {
          if (currentValue.indexOf('Document') !== -1) {
            if (ordersToSign && ordersToSign.length > 0) {
              const documentID = Number(currentValue.split('_')[1]);
              const orderLineIDs = currentValue.split('_')[2];
              const orderLineIDsNumbers = orderLineIDs
                ? orderLineIDs.split('-').map(item => Number(item)) : null;
              ordersToSign.forEach((order, index) => {
                const orderLinesInOrder = orderLineIDsNumbers
                  ? order['Order Line'].filter(item => orderLineIDsNumbers.indexOf(item.ID) !== -1)
                  : null;
                if (orderLinesInOrder && orderLinesInOrder.length > 0) {
                  ordersToPreview[order.ID] = {
                    ...ordersToPreview[order.ID],
                    [`document-${documentID}`]: [
                      ...ordersToPreview[order.ID][`document-${documentID}`] || [],
                      orderLineIDs,
                    ],
                  };
                  if (index === 0) {
                    accumulator = {
                      ...accumulator,
                      [`document-${documentID}`]: [
                        ...accumulator[`document-${documentID}`] || [],
                        orderLineIDs,
                      ]
                    };
                  }
                }
                if (!orderLineIDs) {
                  ordersToPreview[order.ID] = {
                    ...ordersToPreview[order.ID],
                    [`document-${documentID}`]: null,
                  };
                  if (index === 0) {
                    accumulator = {
                      ...accumulator,
                      [`document-${documentID}`]: null,
                    };
                  }
                }
              });
              return accumulator;
            } else {
              const documentID = Number(currentValue.split('_')[1]);
              const orderLineIDs = currentValue.split('_')[2];
              return {
                ...accumulator,
                ...orderLineIDs
                  ? { [`document-${documentID}`]: [
                    ...accumulator[`document-${documentID}`] || [],
                    orderLineIDs,
                  ] }
                  : { [`document-${documentID}`]: null },
              };
            }
          }
          if (currentValue.indexOf('Template') !== -1) {
            const templateID = Number(currentValue.split('_')[1]);
            return {
              ...accumulator,
              ...{ [`template-${templateID}`]: null },
            };
          }
          return accumulator;
        }
        return accumulator;
      }, {});
    const editPreviewHrefParams = qs.stringify(editPreviewItems, {
      encode: false, addQueryPrefix: true,
    });
    let editPreviewHrefPrefix = '';
    let editPreviewHrefStem = '';
    if (variant === 'patient') {
      editPreviewHrefPrefix = `/charts/${patient['Record Number']}/print`;
    }
    if (variant === 'order') {
      editPreviewHrefPrefix = `/workflow/order/print/${ID}`;
    }
    if (Object.keys(editPreviewItems).join().indexOf('document') !== -1) {
      const firstDocumentValue = Object.keys(editPreviewItems)
        .find(item => item.indexOf('document') !== -1);
      const firstDocumentID = firstDocumentValue.replace('document-', '');
      const orderLine = editPreviewItems[firstDocumentValue];
      editPreviewHrefStem = orderLine
        ? `/document/${firstDocumentID}/${orderLine[0]}`
        : `/document/${firstDocumentID}`;
    } else if (Object.keys(editPreviewItems).join().indexOf('template') !== -1) {
      editPreviewHrefStem =
        `/template/${Object.keys(editPreviewItems).find(item => item.indexOf('template') !== -1).replace('template-', '')}`;
    }
    const editPreviewHref = `${editPreviewHrefPrefix}${editPreviewHrefStem}`;
    const historyState = {
      signature: this.canvasRef.toDataURL('image/png'),
      signatureDate: this.props.formValues ? this.props.formValues['Date of Delivery'] : '',
      orders: ordersToPreview,
    };
    const newWindow = window.open(
      `${editPreviewHref}${editPreviewHrefParams}`,
      '_blank',
    );
    if (newWindow) {
      newWindow.historyState = historyState;
    }
  }

  onDoneButtonClick() {
    const { onSubmitSuccess } = this.props;
    onSubmitSuccess({
      signature: this.canvasRef.toDataURL('image/png'),
      ...this.props.formValues && this.props.formValues['Date of Delivery'] ? { signatureDate: this.props.formValues['Date of Delivery'] } : {},
    });
  }

  onDialogEnter() {
    const { ID, orders, actions } = this.props;
    if (ID) {
      actions.fetchOrder({
        ID,
        triggerLoading: false,
      });
    }
    if (orders && orders.length > 0) {
      orders.forEach(order => {
        actions.fetchOrder({
          ID: order.ID,
          triggerLoading: false,
        });
      });
    }
  }

  onDialogEntered() {
    const { initialValues } = this.props;
    if (initialValues && initialValues.signature && this.canvasRef) {
      this.canvasRef.fromDataURL(initialValues.signature);
    }
  }

  render() {
    const {
      open,
      onClose,
      printFormValues,
      initialValues,
      classes,
      variant,
      showDate,
      patient,
      formValues,
      orders,
      width,
    } = this.props;
    const { empty, submitting } = this.state;
    return (
      <Dialog
        open={open}
        onClose={onClose}
        onEnter={this.onDialogEnter}
        onEntered={this.onDialogEntered}
        maxWidth="md"
      >
        <DialogTitle onCloseClick={onClose}>
          <Typography
            variant="title"
            color="secondary"
            align="center"
          >
            {(patient && patient['First Name']) ? `Patient ${patient['First Name']} ${patient['Last Name']}: ` : ''}
            {initialValues && initialValues.signature && initialValues.signature.length > 0
              ? 'Edit Signature' : 'Add Signature'}
          </Typography>
        </DialogTitle>
        <DialogContent disableGutters>
          {(showDate || (orders && orders.length > 0)) &&
            <DocumentsSignDialogForm
              patient={(orders && orders.length > 0) ? orders[0].Patient : patient}
              form="documentSignForm"
              className={classes.form}
              formValues={formValues}
              initialValues={initialValues && initialValues['Sign Order'] ? {
                'Sign Order': initialValues['Sign Order'],
              } : {}}
            />
          }
          <div className={classes.signaturePad}>
            <ReactSignaturePad
              canvasProps={{
                width: 700,
                height: 300,
                ...width === 'xs' ? {
                  width: 350,
                  height: 150,
                } : {},
                className: classes.canvas,
              }}
              clearOnResize={false}
              onEnd={this.onSignEnd}
              minWidth={1.5}
              maxWidth={3}
              ref={(canvasRef) => {
                this.canvasRef = canvasRef;
              }}
            />
          </div>
        </DialogContent>
        <DialogActions
          classes={{
            ...variant !== 'default' ? { action: classes.action } : {},
          }}
        >
          {variant !== 'default' && (
            <Button
              color="primary"
              underline
              disabled={!printFormValues || empty || submitting}
              onClick={this.onEditPreviewClick}
            >
              Edit / Preview
            </Button>
          )}
          <Button
            onClick={() => {
              if (this.canvasRef) {
                this.canvasRef.clear();
                this.setState({ empty: true });
              }
            }}
            variant="raised"
          >
            Clear
          </Button>
          {variant === 'default' && (
            <Button
              onClick={this.onDoneButtonClick}
              variant="raised"
              color="primary"
              disabled={empty}
            >
              Done
            </Button>
          )}
          {variant !== 'default' && (
            <Button
              onClick={this.onSaveButtonClick}
              variant="raised"
              color="primary"
              disabled={!printFormValues || empty || submitting
                || (orders && orders.length > 0 && formValues && formValues['Sign Order'].length === 0)}
              loading={submitting}
            >
              Save all
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }
}


DocumentsSignDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  patient: PropTypes.shape({
    'First Name': PropTypes.string.isRequired,
    'Last Name': PropTypes.string.isRequired,
    'Middle Name': PropTypes.string,
    'Record Number': PropTypes.number.isRequired,
  }),
  templates: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })).isRequired,
  ID: PropTypes.number,
  printFormValues: PropTypes.shape({
    Data: PropTypes.object,
    Documents: PropTypes.object,
    Templates: PropTypes.object,
  }),
  initialValues: PropTypes.shape({
    Signature: PropTypes.string,
  }),
  onSubmitSuccess: PropTypes.func,
  variant: PropTypes.oneOf(['default', 'patient', 'order']),
  showDate: PropTypes.bool,
  classes: PropTypes.shape({
    signaturePad: PropTypes.string.isRequired,
  }).isRequired,
};

DocumentsSignDialog.defaultProps = {
  open: false,
  onClose: () => {},
  onSubmitSuccess: () => {},
  printFormValues: null,
  variant: 'default',
  patient: null,
  ID: null,
  initialValues: null,
  showDate: true,
};

const mapStateToProps = store => ({
  templates: store.templates.data,
  business: store.business.data,
  systemSettings: store.systemSettings.data,
  appInfo: store.appInfo,
  formValues: getFormValues('documentSignForm')(store),
  account: store.account,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    createPatientDocument,
    addNotification,
    fetchOrder,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withWidth()(withStyles(styles)(DocumentsSignDialog)),
);



// WEBPACK FOOTER //
// ./src/components/common/DocumentsSignDialog/DocumentsSignDialog.js